import React, { Component } from 'react'
import { connect } from "react-redux";
import { AxiosResponse } from "axios";
import debounce from "lodash/debounce";
import trans from "trans";
import { RandomProduct } from "../types";
import { URL } from "app/config/api-config";
import { SketchPicker } from "react-color"

import {
    css,
    Button,
    Grid,
    GridCell,
    Theme,
    Icon,
    Spinner,
    Message,
} from "@bdfx/mergado-ui-kit"

import ShapeType, { BADGE_TYPE, BITMAP_TYPE, CUSTOM_SET_TYPE, EXTERNAL_IMAGE_SOURCE_TYPE } from "shape-entities/types/shape";

import {
    getRawSvg,
    getSVGColors,
    replaceSVGColor,
} from "app/helpers/api";

import { ColorType, Cover, Popover } from "./transparent-color";

import { XMLHeader } from "shape-entities/defaults";
import {
    updateInputValue as change,
} from "app/reducers/form";
import { getFormName } from './helpers';
import { iconDisabledColor } from 'template-edit/components/tools-panel';

interface Props {
    isAlt: boolean
    shape: ShapeType
    change: typeof change
    randomProduct: RandomProduct
}

interface State {
    mimeType: string
    xml: any
    fill: string
    color: string
    backgroundColor: string
    fillReplacement: string
    colorsFromFile: string[]
    displayColorPicker: boolean
    zoom: number
    panX: number
    panY: number
    isDragMode: boolean
    isDragging: boolean
    startX: number
    startY: number
    hasChanges: boolean
    showChangeColors: boolean
    isApplyingChanges: boolean
}

const resetState = {
    mimeType: null,
    xml: null,
    fill: null,
    color: null,
    backgroundColor: null,
    colorsFromFile: [],
    isDragMode: false,
    isDragging: false,
    startX: 0,
    startY: 0,
    hasChanges: false,
    isApplyingChanges: false,
}

const iconSize = 34
const PICKER_CURSOR_OFFSET = 12    //gap between click point and picker
const PICKER_VIEWPORT_MARGIN = 10  //min distance from viewport edges
const SVG_XML_MIME_TYPE = 'image/svg+xml'
const SVG_MIME_TYPE = 'image/svg'
// animation elements can set href/event attributes at runtime, so they are dropped as well
const SVG_UNSAFE_TAGS = ['script', 'foreignobject', 'iframe', 'embed', 'object', 'handler', 'listener', 'animate', 'set', 'animatemotion', 'animatetransform', 'discard']
const SVG_UNSAFE_URL = /(javascript|vbscript|data:text\/html)/

// uploaded SVGs are injected via dangerouslySetInnerHTML, so drop anything that can execute script
const sanitizeSvg = (root: Element) => {
    Array.from(root.querySelectorAll('*')).concat(root).forEach((el) => {
        if (SVG_UNSAFE_TAGS.includes(el.localName.toLowerCase())) {
            el.remove()
            return
        }
        Array.from(el.attributes).forEach((attr) => {
            const name = attr.localName.toLowerCase()
            const value = attr.value.replace(/[\s\u0000-\u001f]/g, '').toLowerCase()
            if (name.startsWith('on') || SVG_UNSAFE_URL.test(value)) {
                el.removeAttributeNode(attr)
            }
        })
    })
}

class ChangeSvgColors extends Component<Props, State> {
    selectedElementPath: number[] | null = null
    pickerRef = React.createRef<HTMLDivElement>()
    pickerClickPoint: { x: number, y: number } | null = null

    constructor(props) {
        super(props)

        this.state = {
            ...resetState,
            fillReplacement: null,
            displayColorPicker: false,
            zoom: 1.0,
            panX: 0,
            panY: 0,
            showChangeColors: false,
        }
    }

    componentDidMount(): void {
        if (this.state.showChangeColors) {
            this.getImageFromBlob()
        }
    }

    private getElementPath = (element: Element, root: Element): number[] => {
        const path: number[] = []

        if (root) {
            let current: Element = element
            while (current !== root && current.parentElement) {
                path.unshift(Array.from(current.parentElement.children).indexOf(current))
                current = current.parentElement
            }
        }

        return path
    }

    private getElementByPath = (root: Element, path: number[]): Element | null => {
        if (!root || !path || !path.length) {
            return null
        }

        let current: Element = root
        for (const index of path) {
            if (!current.children[index]) return null
            current = current.children[index]
        }
        return current
    }

    // places the picker next to the click point, flipped/clamped so it stays inside the viewport
    positionColorPicker = () => {
        const picker = this.pickerRef.current
        const point = this.pickerClickPoint

        if (!picker || !point) {
            return
        }

        const parent = (picker.offsetParent as HTMLElement) || document.body
        const parentRect = parent.getBoundingClientRect()
        const width = picker.offsetWidth
        const height = picker.offsetHeight
        const maxX = window.innerWidth - PICKER_VIEWPORT_MARGIN - width
        const maxY = window.innerHeight - PICKER_VIEWPORT_MARGIN - height

        let x = point.x + PICKER_CURSOR_OFFSET
        if (x > maxX) {
            x = point.x - PICKER_CURSOR_OFFSET - width    //flip to the left of the cursor
        }
        x = Math.max(PICKER_VIEWPORT_MARGIN, Math.min(x, maxX))

        let y = point.y + PICKER_CURSOR_OFFSET
        if (y > maxY) {
            y = point.y - PICKER_CURSOR_OFFSET - height   //flip above the cursor
        }
        y = Math.max(PICKER_VIEWPORT_MARGIN, Math.min(y, maxY))

        picker.style.left = `${x - parentRect.left}px`
        picker.style.top = `${y - parentRect.top}px`
        picker.style.visibility = 'visible'
    }

    openColorPicker = (e: React.MouseEvent, fillReplacement: string) => {
        this.pickerClickPoint = { x: e.clientX, y: e.clientY }

        this.setState({
            fillReplacement,
            displayColorPicker: true,
        }, this.positionColorPicker)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (!this.state.showChangeColors) {
            return
        }

        if (!prevState.showChangeColors) {
            this.getImageFromBlob()
        } else if (prevProps && prevProps.shape) {
            const indexChanged = prevProps.shape.index !== this.props.shape.index
            const blobChanged = prevProps.shape.imageBlob !== this.props.shape.imageBlob

            if (indexChanged || blobChanged) {
                if (indexChanged) {
                    this.selectedElementPath = null
                }

                this.setState({...resetState})
                this.getImageFromBlob()
            }
        }
    }

    handleChangeThisColor = (e: React.MouseEvent, colorToBeReplaced: string) => {
        this.selectedElementPath = null    //palette color => replace all occurrences

        this.openColorPicker(e, colorToBeReplaced)
    }

    getColorUnderCursor = (e) => {
        const { isDragMode } = this.state

        if (!isDragMode && e.target) {
            const style = window.getComputedStyle(e.target, null)
            this.setState({
                fill: style.fill,
                color: style.color,
                backgroundColor: style.backgroundColor,
            })
        }
    }

    // reads svg from shape.imageBlob (not from the canvas DOM, which may not be re-rendered yet after apply)
    getImageFromBlob = () => {
        const { shape } = this.props
        const imageBlob: string = shape.imageBlob

        if (!imageBlob) {
            this.setState({ xml: null, mimeType: null })
            return
        }

        const mimeTypeString = imageBlob.slice(0, 50).split(',')[0] || null
        const mimeType = mimeTypeString ? mimeTypeString.replace('data:image/', '').replace(';base64', '') : null

        if (
            (mimeType === 'svg+xml') ||
            (mimeTypeString && mimeTypeString.endsWith('.svg')) ||
            ([CUSTOM_SET_TYPE, EXTERNAL_IMAGE_SOURCE_TYPE].includes(shape.type) && shape.custom_set_file_id && shape.custom_set_id && imageBlob.endsWith('.svg')) ||
            (shape.type === BADGE_TYPE && shape.set_file_id && shape.set_id && imageBlob.endsWith('.svg'))
        ) {
            const request = new XMLHttpRequest()
            request.open('GET', imageBlob)
            request.setRequestHeader("Content-Type", SVG_XML_MIME_TYPE)
            request.addEventListener("load", (event) => {
                if (this.props.shape.imageBlob !== imageBlob) {
                    return  //stale response, a newer blob is being loaded
                }

                const eventTarget: any = event.target
                const response = eventTarget.responseText;

                const doc = new DOMParser();
                const xml: XMLDocument = doc.parseFromString(response, SVG_XML_MIME_TYPE);

                if (xml.getElementsByTagName('parsererror').length > 0) {
                    this.setState({
                        xml: null,
                        mimeType: null,
                    })
                    console.debug('document parse error')
                } else {
                    sanitizeSvg(xml.documentElement)
                    this.setState({ xml: xml.documentElement, mimeType: 'svg+xml' })    //not with image/svg+xml!!!!
                    this.loadColorsFromXml(xml.documentElement)
                }
            })
            request.addEventListener("error", (event) => {
                console.error('cannot read xml', event)
                console.debug(`cannot read xml: shape type is ${shape.type}, mime type: '${mimeType}', custom set file: ${shape.custom_set_file_id}, custom set: ${shape.custom_set_id}, set file: ${shape.set_file_id}, set id: ${shape.set_id}`, event)
            })

            request.send();
        }
        this.setState({ mimeType: mimeType })
    }

    loadColorsFromXml = (xml: Element) => {
        getSVGColors(xml.outerHTML)
            .then((response: AxiosResponse<any>) => {
                this.setState({ colorsFromFile: Object.values(response.data.colors) })
            })
            .catch((e) => {
                console.error('cannot get colors from svg file', e)
            })
    }

    handleMouseMove = (e) => {
        const { isDragging, startX, startY } = this.state

        if (isDragging && e.button === 0) {
            this.setState({
                isDragMode: true,
                panX: e.clientX - startX,
                panY: e.clientY - startY,
            })
        } else {
            this.setState({
                isDragging: false,
                isDragMode: false,
            })
        }
    }

    handleMouseDown = (e) => {
        const { panX, panY } = this.state

        if (e.button === 0) {   //left button
            e.preventDefault()

            this.setState({
                isDragging: true,
                startX: e.clientX - panX,
                startY: e.clientY - panY,
            })
        }
    }

    handleMouseUp = (e) => {
        this.setState({
            isDragging: false,
            isDragMode: false,
        })
    }

    handleClickOnColor = (e) => {
        const { isDragMode } = this.state

        if (!isDragMode && e.target) {
            const style = window.getComputedStyle(e.target, null)

            const svgElement = document.getElementById('svg-color-pick')
            if (svgElement && e.target !== svgElement) {
                this.selectedElementPath = this.getElementPath(e.target, svgElement)
            }

            this.openColorPicker(e, style.fill)
        }
    }

    // changes are applied only to the local preview (state.xml), canvas is updated in handleButtonClicked
    handleChangeComplete = debounce((color: ColorType) => {
        const { xml, fillReplacement } = this.state

        if (!xml) {
            return
        }

        const a = Math.round(color.rgb.a * 255).toString(16);
        const finalHexColor = color.hex + (color.rgb.a !== 1 ? (a.length === 1 ? "0" + a : a) : '')

        const newXml = xml.cloneNode(true) as Element
        const element = this.getElementByPath(newXml, this.selectedElementPath) as any

        if (element) {
            element.style.fill = finalHexColor
            this.setState({
                xml: newXml,
                fillReplacement: finalHexColor,
                hasChanges: true,
            })
        } else {    //no reference => replace all!
            console.debug(`xml searching for ${fillReplacement} change to ${finalHexColor}`)

            replaceSVGColor(XMLHeader + xml.outerHTML, fillReplacement, finalHexColor)
                .then((response: any) => {
                    const parsed = new DOMParser().parseFromString(response.data.svg, SVG_XML_MIME_TYPE)
                    if (parsed.getElementsByTagName('parsererror').length > 0) {
                        console.debug('document parse error')
                        return
                    }
                    sanitizeSvg(parsed.documentElement)

                    this.setState({
                        xml: parsed.documentElement,
                        fillReplacement: finalHexColor,
                        hasChanges: true,
                    })
                    this.loadColorsFromXml(parsed.documentElement)
                })
                .catch(e => {
                    console.debug('error while replacing color', e)
                })
        }
    }, 10) //wait for randomProductColors

    handleButtonClicked = () => {
        const { shape, isAlt } = this.props
        const { xml, hasChanges } = this.state
        const formName = getFormName(isAlt)

        const isDataSvg = this.getMimeFromImageBlob().includes(SVG_XML_MIME_TYPE)

        const updatedShape = {
            ...shape,
            type: BITMAP_TYPE,
            custom_set_id: shape.type === EXTERNAL_IMAGE_SOURCE_TYPE ? shape.custom_set_id : null,
            custom_set_file_id: shape.type === EXTERNAL_IMAGE_SOURCE_TYPE ? shape.custom_set_file_id : null,
            set_id: null,
            set_file_id: null,
        }

        console.debug(`applying changes for ${shape.type} --> ${BITMAP_TYPE}`, {
            type: shape.type,
            xml: !!xml,
            hasChanges: hasChanges,
            isDataSvg,
            updatedShapeWithoutImageBlob: updatedShape,
        })

        if (xml && hasChanges) {
            const imageBlob = `data:${SVG_XML_MIME_TYPE},${encodeURIComponent(XMLHeader + xml.outerHTML)}`

            this.props.change(formName, `shapes.${shape.index}`, {
                ...updatedShape,
                imageBlob,
            })
        } else if (!isDataSvg) {    //svg file path => switch to editable data svg
            this.setState({isApplyingChanges: true})

            getRawSvg(shape.imageBlob)
                .then((response: AxiosResponse<any>) => {
                    const rawFile = response.data?.rawFile
                    if (!rawFile) {
                        console.error('error: raw svg is empty, keeping original imageBlob', response.data)
                        return
                    }

                    const imageBlob = `data:${SVG_XML_MIME_TYPE},${encodeURIComponent(rawFile)}`

                    this.props.change(formName, `shapes.${shape.index}`, {
                        ...updatedShape,
                        imageBlob,
                    })
                })
                .catch(e => {
                    console.error('error: cannot get raw svg', e)
                })
                .finally(() => this.setState({isApplyingChanges: false}))
        } else {
            console.debug(`apply changes, other option`)
        }
    }

    getMimeFromImageBlob = () : string => {
        const { shape } = this.props
        return shape?.imageBlob ? shape.imageBlob.substring(5, 18) : ''
    }

    render() {
        const { shape, randomProduct } = this.props
        const { fill, fillReplacement, colorsFromFile, xml, zoom, panX, panY, isDragMode, isDragging, displayColorPicker, mimeType, hasChanges, showChangeColors, isApplyingChanges } = this.state

        const mime = this.getMimeFromImageBlob()

        if (!(shape.imageBlob && shape.imageBlob.length > 32 && (mime.includes(SVG_MIME_TYPE) || shape.imageBlob.endsWith('.svg')))) {
            return <div />
        }

        if (!showChangeColors) {
            return (
                <StyledDiv>
                    <Button onClick={() => this.setState({ showChangeColors: true })}>
                        {trans('svg_change_colors')}
                    </Button>
                </StyledDiv>
            )
        }

        const randomProductColors = randomProduct && randomProduct.palette ? randomProduct.palette : []

        return (
            <StyledDiv>
                {!xml ?
                    <Message type='warning' text={trans('load_image_error')} /> :
                    <StyledGrid cols={'auto'}>
                        <GridCell style={{ paddingBottom: '10px', paddingTop: '20px' }}>
                            {Array.isArray(colorsFromFile) &&
                                <Grid cols={'repeat(auto-fit, minmax(22px, 1fr))'} gap={'1px'}>
                                    {colorsFromFile.map((color: string, idx: number) => {
                                        return <GridCell key={idx}>
                                            <div
                                                title={color}
                                                className={'shadow-box'}
                                                onClick={(e) => this.handleChangeThisColor(e, color)}
                                                style={{
                                                    backgroundColor: color,
                                                    textAlign: 'center',
                                                    alignSelf: 'center',
                                                    height: '18px',
                                                    width: '18px',
                                                    borderRadius: '18px',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </GridCell>
                                    })}
                                </Grid>
                            }
                        </GridCell>

                        {fillReplacement && displayColorPicker &&
                            <GridCell>
                                <Popover />
                                <Cover className={'cover'} onClick={() => this.setState({ displayColorPicker: false })} />
                                <div ref={this.pickerRef} className={'color-picker-wrapper'}>
                                    <SketchPicker
                                        disableAlpha
                                        color={fillReplacement}
                                        onChangeComplete={this.handleChangeComplete}
                                        style={{ width: '100%' }}
                                        presetColors={randomProductColors}
                                    //presetColors={colorsFromFile.slice(0,16)}    //max 16 preset colors
                                    />
                                </div>

                            </GridCell>
                        }

                        <StyledGridCell
                            className={`shadow-box checker ${isDragMode ? 'drag-mode' : (isDragging ? 'grab-mode' : 'pick-mode')}`}
                            onMouseDown={this.handleMouseDown}
                            onMouseUp={this.handleMouseUp}
                            onMouseMove={this.handleMouseMove}
                            style={{ position: 'relative' }}
                        >
                            <StyledButtonsGrid cols={'repeat(4, 1fr)'} gap={'5px'}>
                                <GridCell>
                                    <span style={{ cursor: 'zoom-out' }} onClick={() => this.setState({ zoom: zoom / 1.2 })}>
                                        <Icon type={'zoom-out-light'} size={iconSize} color={Theme.blue} />
                                    </span>
                                </GridCell>
                                <GridCell>
                                    <span className={'magnification'}>{Math.round(zoom * 10) / 10} ×</span>
                                </GridCell>
                                <GridCell>
                                    <span style={{ cursor: 'zoom-in' }} onClick={() => this.setState({ zoom: zoom * 1.2 })}>
                                        <Icon type={'zoom-in-light'} size={iconSize} color={Theme.blue} />
                                    </span>
                                </GridCell>
                                <GridCell>
                                    {(zoom !== 1 || panX !== 0 || panY !== 0) &&
                                        <span style={{ cursor: 'move' }} title={`[${panX}, ${panY}]`}
                                            onClick={() => this.setState({ zoom: 1, panX: 0, panY: 0 })}>
                                            <Icon type={'arrows'} size={iconSize} color={Theme.blue} />
                                        </span>
                                    }
                                </GridCell>
                            </StyledButtonsGrid>

                            {fill &&
                                <StyledSmallColorGrid cols={'repeat(1, 1fr)'} gap={'5px'}>
                                    <GridCell style={{ cursor: 'pointer', backgroundColor: fill || 'transparent', alignSelf: 'center', textAlign: 'center' }}>
                                        <div title={fill} style={{ width: '100%', height: '100%' }} onClick={(e) => this.handleChangeThisColor(e, fill)} />
                                    </GridCell>
                                </StyledSmallColorGrid>
                            }

                            <svg
                                id="svg-color-pick"
                                key={`svg_${shape.index}_${shape.imageBlob.length}_${zoom}`}
                                preserveAspectRatio={'xMidYMid slice'}
                                viewBox={xml.getAttribute('viewBox') || `0 0 ${shape.width} ${shape.height}`}
                                dangerouslySetInnerHTML={{ __html: xml.innerHTML }}
                                onMouseOver={this.getColorUnderCursor}
                                onClick={(e) => this.handleClickOnColor(e)}
                                transform={`scale(${zoom}), translate(${panX}, ${panY})`}
                            />
                        </StyledGridCell>

                        <Button
                            type={'void'}
                            color={hasChanges ? Theme.steelblue : iconDisabledColor}
                            disabled={isApplyingChanges || (!!xml && mimeType === 'svg+xml' && !hasChanges)} onClick={(e) => this.handleButtonClicked()}
                        >
                            {isApplyingChanges
                                ? <Spinner type={'bubbles'} />
                                : <React.Fragment>{trans('apply_color_change')} {mimeType && mimeType.length < 16 ? `(${mimeType})` : ''}</React.Fragment>
                            }
                        </Button>
                    </StyledGrid>
                }
            </StyledDiv>
        )
    }
}

const StyledGrid = css(Grid)({
    gridGap: '15px',
    ' .drag-mode': {
        cursor: 'move',
    },
    ' .pick-mode': {
        //cursor: 'pointer',
    },
    ' .grab-mode': {
        cursor: 'grab',
    },
    position: 'relative',
    ' .color-picker-wrapper': {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 999,
        visibility: 'hidden',   //shown by positionColorPicker() once placed
    }
})

const StyledGridCell = css(GridCell)({
    textAlign: 'center',
    overflow: 'hidden',
    paddingTop: '20px',
    ' svg': {
        pointerEvents: 'none',
        cursor: 'not-allowed',
        ' path, rectangle, circle, polygon': {
            pointerEvents: 'auto',
            cursor: `url("${URL.publicPath}img/colorpicker.cur") -16 16, pointer`
        },
    },
})

const StyledSmallColorGrid = css(Grid)({
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 99,
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
        width: '20px',
        height: '20px',
        verticalAlign: 'middle',
        borderRadius: '3px',
    },
})

const StyledButtonsGrid = css(Grid)({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 99,
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' .coordinates, .magnification': {
        whiteSpace: 'nowrap',
        color: Theme.blue,
    },
    ' .clickable': {
        cursor: 'pointer',
    },
    ' .muk-icon': {
        ' svg path': {
            fill: '#ffffff',
            stroke: '#3f3f3f',
            strokeWidth: 1,
        },
    },
})

const StyledDiv = css('div')({
    textAlign: 'center',
    alignSelf: 'center',
    padding: '15px',
})


function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        randomProduct: state.randomProduct,
    }
}

export default connect(mapStateToProps, {
    change,
})(ChangeSvgColors)